/**
 * @boli/core - Store Service
 *
 * Platform-agnostic store management for Boli Live seller storefronts.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 */

import type {
  Store,
  Product,
  Auction,
  CreateStoreRequest,
  UpdateStoreRequest,
  ProductQueryParams,
  ProductListResponse,
  ApiResponse,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IStoreService {
  /**
   * Get a store by its URL slug
   */
  getStore(slug: string): Promise<Store | null>;

  /**
   * Get a store's products
   */
  getStoreProducts(slug: string, params?: ProductQueryParams): Promise<ProductListResponse>;

  /**
   * Get a store's auctions
   */
  getStoreAuctions(slug: string, params?: StoreAuctionParams): Promise<StoreAuctionResponse>;

  /**
   * Get the current user's store
   */
  getMyStore(): Promise<Store | null>;

  /**
   * Create a new store (requires authentication)
   */
  createStore(data: CreateStoreRequest): Promise<ApiResponse<Store>>;

  /**
   * Update the current user's store (requires authentication)
   */
  updateStore(data: UpdateStoreRequest): Promise<ApiResponse<Store>>;
}

export interface StoreServiceConfig {
  apiBaseUrl: string;
}

export interface StoreAuctionParams {
  status?: string;
  limit?: number;
  offset?: number;
}

export interface StoreAuctionResponse {
  auctions: Partial<Auction>[];
  total: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_STORE_CONFIG: StoreServiceConfig = {
  apiBaseUrl: '',
};

// ============================================================================
// STORE SERVICE IMPLEMENTATION
// ============================================================================

export class StoreService implements IStoreService {
  private config: StoreServiceConfig;
  protected fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  protected getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<StoreServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_STORE_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authenticated headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Get a store by its URL slug
   */
  async getStore(slug: string): Promise<Store | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/stores/${slug}`
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch store');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get store:', error);
      return null;
    }
  }

  /**
   * Get a store's products
   */
  async getStoreProducts(
    slug: string,
    params?: ProductQueryParams
  ): Promise<ProductListResponse> {
    try {
      const queryParts: string[] = [];
      if (params?.limit !== undefined) queryParts.push(`limit=${params.limit}`);
      if (params?.offset !== undefined) queryParts.push(`offset=${params.offset}`);
      if (params?.category) queryParts.push(`category=${encodeURIComponent(params.category)}`);
      if (params?.sort) queryParts.push(`sort=${encodeURIComponent(params.sort)}`);

      const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/stores/${slug}/products${queryString}`
      );

      if (!response.ok) {
        return {
          products: [],
          total: 0,
          limit: params?.limit || 50,
          offset: params?.offset || 0,
        };
      }

      const data = await response.json();
      return {
        products: data.products || [],
        total: data.total || 0,
        limit: params?.limit || 50,
        offset: params?.offset || 0,
      };
    } catch (error) {
      console.error('Failed to fetch store products:', error);
      return {
        products: [],
        total: 0,
        limit: params?.limit || 50,
        offset: params?.offset || 0,
      };
    }
  }

  /**
   * Get a store's auctions
   */
  async getStoreAuctions(
    slug: string,
    params?: StoreAuctionParams
  ): Promise<StoreAuctionResponse> {
    try {
      const queryParts: string[] = [];
      if (params?.status) queryParts.push(`status=${encodeURIComponent(params.status)}`);
      if (params?.limit !== undefined) queryParts.push(`limit=${params.limit}`);

      const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/stores/${slug}/auctions${queryString}`
      );

      if (!response.ok) {
        return {
          auctions: [],
          total: 0,
        };
      }

      const data = await response.json();
      return {
        auctions: data.auctions || [],
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Failed to fetch store auctions:', error);
      return {
        auctions: [],
        total: 0,
      };
    }
  }

  /**
   * Get the current user's store
   */
  async getMyStore(): Promise<Store | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/stores/my`,
        { headers }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch your store');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get my store:', error);
      return null;
    }
  }

  /**
   * Create a new store
   */
  async createStore(data: CreateStoreRequest): Promise<ApiResponse<Store>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/stores`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'CREATE_FAILED',
            message: responseData.message || 'Failed to create store',
          },
        };
      }

      return {
        success: true,
        data: responseData.store,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Update the current user's store
   */
  async updateStore(data: UpdateStoreRequest): Promise<ApiResponse<Store>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/stores/my`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'UPDATE_FAILED',
            message: responseData.message || 'Failed to update store',
          },
        };
      }

      return {
        success: true,
        data: responseData.store,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Check if a store slug is available
   */
  async isSlugAvailable(slug: string): Promise<boolean> {
    const store = await this.getStore(slug);
    return store === null;
  }

  /**
   * Generate a URL-friendly slug from a store name
   */
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove non-word chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/--+/g, '-') // Replace multiple hyphens with single
      .substring(0, 50); // Limit length
  }
}

// ============================================================================
// STORE SERVICE FACTORY
// ============================================================================

let storeServiceInstance: StoreService | null = null;

export function createStoreService(
  config: Partial<StoreServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): StoreService {
  storeServiceInstance = new StoreService(config, fetchAdapter, getAuthToken);
  return storeServiceInstance;
}

export function getStoreService(): StoreService {
  if (!storeServiceInstance) {
    throw new Error('StoreService not initialized. Call createStoreService first.');
  }
  return storeServiceInstance;
}
